/**
 * Representa el registro de abandono de mercancía de un lote, con sus fechas,
 * porcentajes, tonelajes y archivos soporte.
 */
export interface Abandono {
  /** Identificador del registro de abandono. */
  id?:                                number;
  /** Identificador del lote asociado. */
  batch_id:                           number;
  /** Fecha de desatraque (ISO 8601). */
  undocking_date:                     string;
  /** Fecha en que se declara el abandono (ISO 8601). */
  abandonment_date:                   string;
  /** Porcentaje de mercancía en abandono. */
  merchandise_abandonment_percentage: number;
  /** Tonelaje total en abandono. */
  abandonment_total_tonnage:          number;
  /** Número de oficio CTS asociado. */
  trade_number_cts:                   string;
  /** Número de oficio de la aduana. */
  customs_office_number:              string;
  /** Tonelaje declarado. */
  declared_tonnage:                   number;
  /** Total de piezas en abandono. */
  abandonment_total_pieces:           number;
  /** Clave/ruta del primer archivo soporte. */
  file1:                              string;
  /** Clave/ruta del segundo archivo soporte. */
  file2:                              string;
  /** Clave/ruta del tercer archivo soporte. */
  file3:                              string;
}

/** Datos requeridos para crear un registro de abandono de mercancía. */
export interface AbandonoCreate {
  /** Identificador del lote asociado. */
  batch_id:                           number;
  /** Fecha de desatraque (ISO 8601). */
  undocking_date:                     string;
  /** Fecha en que se declara el abandono (ISO 8601). */
  abandonment_date:                   string;
  /** Porcentaje de mercancía en abandono. */
  merchandise_abandonment_percentage: number;
  /** Número de oficio CTS asociado. */
  trade_number_cts:                   string;
  /** Número de oficio de la aduana. */
  customs_office_number:              string;
  /** Clave/ruta del primer archivo soporte. */
  file1:                              string;
  /** Clave/ruta del segundo archivo soporte. */
  file2:                              string;
  /** Clave/ruta del tercer archivo soporte. */
  file3:                              string;
}

/** Información resumida del abandono: fecha, tonelaje restante y porcentaje. */
export interface AbandonoInfo {
  /** Fecha del abandono (ISO 8601). */
  abandonment_date: string;
  /** Tonelaje restante tras el abandono. */
  remaining_tonnage: number;
  /** Porcentaje total acumulado. */
  percent_total: number;
}

/** Encapsula únicamente la fecha de abandono. */
export interface AbandonoDate {
  /** Fecha del abandono (ISO 8601). */
  abandonment_date: string;
}

/** Identificador de abandono con bandera y mensaje de error opcionales. */
export interface AbandonoId {
  /** Identificador del registro de abandono. */
  id: number;
  /** Indica si la operación produjo error. */
  error?: boolean;
  /** Mensaje descriptivo del resultado. */
  message?: string;
}

/** Referencia a un archivo soporte de abandono por id y consecutivo. */
export interface AbandonoFile {
  /** Identificador del registro de abandono. */
  id:          number;
  /** Número consecutivo del archivo. */
  consecutive: number;
}

/** Respuesta de descarga de archivo con URL, bandera y mensaje de error. */
export interface FileResponse {
  /** URL para acceder al archivo. */
  url: string;
  /** Indica si la operación produjo error. */
  error: boolean,
  /** Mensaje descriptivo del resultado. */
  message: string
}

/** Representa un pedimento aduanal asociado a un lote de mercancía. */
export interface Pedimento {
  /** Número de patente del agente aduanal. */
  no_customs_patent:              number;
  /** Número de pedimento aduanal. */
  number_customs_entry:           string;
  /** Toneladas liberadas en el pedimento. */
  tons_released:                  number;
  /** Cantidad de big bags (puede ser nula). */
  big_bags:                       number | null;
  /** Fecha de pago del pedimento (ISO 8601). */
  customs_entry_payment_date:     string;
  /** Agencia aduanal. */
  customs_agency:                 string;
  /** Clave del pedimento. */
  customs_entry_key:              string;
  /** Nombre del documento del pedimento (puede ser nulo). */
  name_customs_entry_document:    string | null;
  /** Identificador del usuario que crea el registro. */
  user_create_id:                 string;
  /** Identificador del usuario que actualiza el registro. */
  user_update_id:                 string;
  /** Número de registro asociado. */
  registry_number:                number;
  /** Estatus de modulación. */
  modulation_status:              string;
  /** Identificador del lote asociado. */
  batch_id:                       number;
  /** Toneladas liberadas para esta entrada de pedimento. */
  custom_entry_tons_released:     number;
  /** Identificador del pedimento. */
  id:                             number;
  /** Descripción de la clave del pedimento. */
  custom_entry_key_description?:  string;
  /** Descripción del estatus de modulación. */
  modulation_status_description?: string;
  /** Valor comercial declarado. */
  commercial_value?:              number;
  /** Identificador actual asociado. */
  current_id?:                    number;
  /** Identificador del tipo de solicitud. */
  request_type_id?:               number;
  /** Identificador anterior (puede ser nulo). */
  old_id:                         number | null;
  /** Indica si el pedimento está activo. */
  active:                         boolean;
  /** Número de barco asociado. */
  ship_number:                    number;
}

/** Datos requeridos para crear un pedimento aduanal. */
export interface PedimentoCreate {
  /** Número de patente del agente aduanal. */
  no_customs_patent:              number;
  /** Número de pedimento aduanal. */
  number_customs_entry:           number;
  /** Toneladas liberadas en el pedimento. */
  tons_released:                  number;
  /** Cantidad de big bags (puede ser nula). */
  big_bags:                       number | null;
  /** Fecha de pago del pedimento (ISO 8601). */
  customs_entry_payment_date:     string;
  /** Agencia aduanal. */
  customs_agency:                 string;
  /** Clave del pedimento. */
  customs_entry_key:              number;
  /** Nombre del documento del pedimento (puede ser nulo). */
  name_customs_entry_document:    string | null;
  /** Identificador del usuario que crea el registro. */
  user_create_id:                 string;
  /** Identificador del usuario que actualiza el registro. */
  user_update_id:                 string;
  /** Estatus de modulación. */
  modulation_status:              number;
  /** Identificador del lote asociado (puede ser nulo). */
  batch_id:                       number | null;
  /** Número de barco asociado. */
  ship_number:                    number;
  /** Identificador del pedimento. */
  id?:                            number;
  /** Clave/ruta del archivo soporte. */
  file?:                          string;
}

/** Pedimento listado por lote, con campos comentados según su equivalencia funcional. */
export interface PedimentoByBatch {
  /** Identificador del pedimento. */
  id:                   number;
  /** Número de pedimento (número de solicitud). */
  number_customs_entry: number;//request_number
  /** Agencia aduanal (agente aduanal). */
  customs_agency:       string;//customs_agent
  /** Identificador de la clave (clave de solicitud). */
  key_id:               string;//key_request
  /** Barco asociado (número-nombre). */
  ship:                 string;//number-name
}

/** Identificador de pedimento. */
export interface PedimentoId {
  /** Identificador del pedimento. */
  id_customs_entry: number;
}

/** Referencia a un archivo de pedimento por identificador. */
export interface PedimentoFile {
  /** Identificador del pedimento. */
  id_customs_entry:   number;
  /** Clave/ruta del archivo soporte. */
  file?:              string;
}

/** Respuesta de descarga de archivo de pedimento. */
export interface GetPedimentoFile {
  /** URL para acceder al archivo. */
  url:                      string;
  /** Indica si la operación produjo error. */
  error:                    boolean;
  /** Mensaje descriptivo del resultado. */
  message:                  string;
}

/** Respuesta genérica de operaciones sobre pedimentos. */
export interface PedimentoResponse {
  /** Mensaje descriptivo del resultado. */
  message:                  string;
  /** Indica si la operación produjo error. */
  error:                    boolean;
}

/** Pedimento activo con datos básicos de identificación. */
export interface ActiveCustom {
  /** Identificador del pedimento. */
  id:                   number;
  /** Número de pedimento aduanal. */
  number_customs_entry: number;
  /** Agencia aduanal. */
  customs_agency:       string;
  /** Identificador de la clave del pedimento. */
  key_id:               string;
}
